import React, { useLayoutEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { IPDNotification } from '../../types/PDNotification/PDNotification';
import date from 'date-and-time';
import { useTheme } from '../../contexts/ThemeContext';

interface FaultHeatmapProps {
  faults: IPDNotification[];
  onCellClick?: (date?: Date) => void;
}

export function FaultHeatmap({ faults, onCellClick }: FaultHeatmapProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const xAxisRef = useRef<any>(null);
  const yAxisRef = useRef<any>(null);
  const seriesRef = useRef<any>(null);
  const {
    theme: { isDark, light, dark },
  } = useTheme();

  const getThemeColors = () => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    return {
      labelColor: isDarkMode ? 0xd1d5db : 0x374151,
      emptyFillColor: isDarkMode ? 0x161b22 : 0xebedf0,
    };
  };

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    // Get theme primary color from CSS variable
    const primaryColor = isDark ? dark.primary : light.primary;
    const { r, g, b } = primaryColor;

    // Create root element
    const root = am5.Root.new(chartRef.current);

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Remove default background
    root.container.set(
      'background',
      am5.Rectangle.new(root, {
        fill: am5.color(0xffffff),
        fillOpacity: 0,
      })
    );

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout,
        paddingBottom: 28,
      })
    );

    // Create X axis for weeks
    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 12,
      opposite: true,
    });
    xRenderer.grid.template.set('strokeOpacity', 0);

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: xRenderer,
        categoryField: 'week',
      })
    );
    xAxisRef.current = xAxis;

    // Create Y axis for days of week
    const yRenderer = am5xy.AxisRendererY.new(root, {
      minGridDistance: 1,
      cellStartLocation: 0.02,
      cellEndLocation: 0.98,
    });
    yRenderer.grid.template.set('strokeOpacity', 0);

    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: yRenderer,
        categoryField: 'dayOfWeek',
      })
    );
    yAxisRef.current = yAxis;

    // Generate GitHub-style contribution data
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Find the Sunday of the current week
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - today.getDay());

    // Start from 52 weeks before the current week (53 weeks total including current)
    const startDate = new Date(currentWeekStart);
    startDate.setDate(currentWeekStart.getDate() - 51 * 7);

    // Group faults by date string
    const faultCounts: { [key: string]: number } = {};
    faults.forEach((fault) => {
      const faultDate = new Date(fault.timestamp);
      faultDate.setHours(0, 0, 0, 0);
      const dateKey = faultDate.toISOString().split('T')[0];
      faultCounts[dateKey] = (faultCounts[dateKey] || 0) + 1;
    });

    // Create data array for GitHub-style grid
    const data: {
      week: string;
      dayOfWeek: string;
      value: number;
      dateStr: string;
      date: Date;
    }[] = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    let currentDate = new Date(startDate);
    let weekNum = 0;
    const monthPositions: { [key: number]: string } = {};

    // Generate 52 weeks of data (including current week)
    while (weekNum < 52) {
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const dateKey = currentDate.toISOString().split('T')[0];
        const isInRange = currentDate <= today;

        // Track month labels for first occurrence
        if (currentDate.getDate() === 1 && !monthPositions[weekNum]) {
          monthPositions[weekNum] = monthNames[currentDate.getMonth()];
        }

        // Use the actual day of week from the current date instead of dayIndex
        const actualDayOfWeek = currentDate.getDay();

        // Create date at noon UTC to avoid DST issues
        const dateAdj = new Date(currentDate);
        dateAdj.setHours(12, 0, 0, 0);

        data.push({
          week: `Week ${weekNum}`,
          dayOfWeek: dayNames[actualDayOfWeek],
          value: isInRange ? faultCounts[dateKey] || 0 : 0,
          dateStr: date.format(currentDate, 'DD/MM/YYYY'),
          date: dateAdj,
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }
      weekNum++;
    }

    // Set up axis data
    const weeks = Array.from({ length: 53 }, (_, i) => ({
      week: `Week ${i}`,
      monthLabel: monthPositions[i] || '',
    }));

    xAxis.data.setAll(weeks);

    const days = dayNames.map((day) => ({ dayOfWeek: day }));
    yAxis.data.setAll(days.reverse());

    // Style labels for dark/light mode
    const { labelColor } = getThemeColors();

    // Add month labels to x-axis
    xAxis
      .get('renderer')
      .labels.template.adapters.add('text', (text, target) => {
        const dataItem = target.dataItem as any;
        if (dataItem) {
          const dataContext = dataItem.dataContext;
          if (dataContext && dataContext.monthLabel) {
            return dataContext.monthLabel;
          }
        }

        return '';
      });

    xAxis.get('renderer').labels.template.setAll({
      fontSize: 9,
      fill: am5.color(labelColor),
    });

    yAxis.get('renderer').labels.template.setAll({
      fontSize: 9,
      fill: am5.color(labelColor),
    });

    // Create series
    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        xAxis: xAxis,
        yAxis: yAxis,
        categoryXField: 'week',
        categoryYField: 'dayOfWeek',
        valueField: 'value',
      })
    );
    seriesRef.current = series;

    // Set up cell styling - GitHub style
    series.columns.template.setAll({
      width: 18,
      height: 18,
      tooltipText: '{dateStr}: {value} faults',
      strokeWidth: 0,
      strokeOpacity: 0,
      cornerRadiusTL: 1,
      cornerRadiusTR: 1,
      cornerRadiusBL: 1,
      cornerRadiusBR: 1,
    });

    // Add click handler for cells with values
    series.columns.template.events.on('click', function (ev) {
      const dataItem = ev.target.dataItem;

      if (dataItem && onCellClick) {
        const dataContext = dataItem.dataContext as any;
        if (dataContext) {
          onCellClick(dataContext.date);
        }
      }
    });

    // Set cursor to pointer for cells with values
    series.columns.template.set('cursorOverStyle', 'pointer');

    const maxValue = Math.max(...data.map((d) => d.value));

    // Set data
    series.data.setAll(data);

    // Disable tooltips for cells with no faults
    series.columns.template.adapters.add('tooltipText', (text, target) => {
      const dataItem = target.dataItem;
      if (dataItem) {
        const dataContext = dataItem.dataContext as any;
        const value = dataContext.value;
        // Return empty string (no tooltip) for cells with 0 faults
        if (value === 0) {
          return '';
        }
      }
      return text;
    });

    // GitHub-style color scheme: light gray to primary color
    series.columns.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      if (dataItem) {
        const dataContext = dataItem.dataContext as any;
        const value = dataContext.value;
        const { emptyFillColor } = getThemeColors();

        if (value === 0) {
          // Light gray for empty cells (like GitHub)
          return am5.color(emptyFillColor);
        }

        if (maxValue === 0) {
          return am5.color(emptyFillColor);
        }

        // Create 4 intensity levels like GitHub
        const ratio = value / maxValue;
        let intensity: number;

        if (ratio <= 0.25) intensity = 0.25;
        else if (ratio <= 0.5) intensity = 0.5;
        else if (ratio <= 0.75) intensity = 0.75;
        else intensity = 1;

        // Interpolate from light to primary color
        const finalR = Math.round(255 + (r - 255) * intensity);
        const finalG = Math.round(255 + (g - 255) * intensity);
        const finalB = Math.round(255 + (b - 255) * intensity);

        const hexColor = (finalR << 16) | (finalG << 8) | finalB;
        return am5.color(hexColor);
      }
      return fill;
    });

    return () => {
      root.dispose();
    };
  }, [faults, isDark, light, dark]);

  return (
    <div
      ref={chartRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '205px',
        zIndex: 0,
      }}
    />
  );
}
